import { jwtDecode } from 'jwt-decode';
import React, { Component } from "react";
import logo from "../../assets/img/logo.png";
import { showNotificationGlobal } from "../../components/common/NotificationProvider";
import withNavigate from "../../components/withNavigate";
import LoginService from "../../services/api/login";
import UserService from "../../services/api/user";
import { ACCESS_TOKEN, SIGNIN_AUTH_TOKEN } from "../../settings/shared/constants";
import { logoutAndPreserveConsent } from '../../settings/shared/commonMethods';
import {
    completeDesktopOAuth,
    isDesktopOAuth,
} from '../../settings/shared/desktopOAuth';

class ChooseTenant extends Component {
    constructor(props) {
        super(props);
        this.state = {
            tenants: [],
            loading: true,
            timeLeft: 60,
            userEmail: '',
            error: null,
            tenantSearch: '',
            selectedTenant: null,
        };
        this.timer = null;
    }

    componentDidMount() {
        this.checkAuth();
        this.startTimer();
        let states = null;
        if (window.history.state?.usr) states = window.history.state.usr;
        // Persist tenant list for in-app tenant switching later
        try {
            if (states?.tenantList) localStorage.setItem("tenantList", JSON.stringify(states.tenantList));
            if (states?.email) localStorage.setItem("loginEmail", states.email);
        } catch (e) {}
        this.setState({ tenants: states?.tenantList || [], userEmail: states?.email || '', loading: false });
    }

    componentWillUnmount() {
        if (this.timer) clearInterval(this.timer);
    }

    componentDidUpdate() { this.checkAuth(); }

    checkAuth = () => {
        if (!sessionStorage.getItem(SIGNIN_AUTH_TOKEN))
            this.props.navigate("/auth/login", { replace: true });
    };

    startTimer = () => {
        this.timer = setInterval(() => {
            this.setState(prev => {
                const newTime = prev.timeLeft - 1;
                if (newTime <= 0) { this.props.navigate('/auth/login'); return { timeLeft: 0 }; }
                return { timeLeft: newTime };
            });
        }, 1000);
    };

    handleCancel = () => {
        showNotificationGlobal("Your last session has ended", "error");
        logoutAndPreserveConsent();
    };

    formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    getTimerUrgency = () => {
        const { timeLeft } = this.state;
        if (timeLeft <= 10) return 'urgent';
        if (timeLeft <= 30) return 'warning';
        return 'normal';
    };

    filterTenants = (list, query) => {
        const q = (query || '').trim().toLowerCase();
        if (!q) return list || [];
        return (list || []).filter(t => {
            const name = (t?.accountName || '').toLowerCase();
            const id = String(t?.accountID ?? '').toLowerCase();
            return name.includes(q) || id.includes(q);
        });
    };

    clearTenantSearch = () => this.setState({ tenantSearch: '' });

    finishDesktopLogin = async (accessToken) => {
        showNotificationGlobal("Opening SuperAgentX Desktop…", "success");
        await completeDesktopOAuth({
            accessToken,
            authorizeFn: (payload) => LoginService.authorizeDesktop(payload),
        });
    };

    handleTenantSelect = async (tenant) => {
        if (!tenant) return;
        this.setState({ selectedTenant: tenant.accountID });
        try {
            const postValidate = { email: this.state.userEmail.toLowerCase(), accountId: tenant?.accountID };
            localStorage.setItem("accountLogo", tenant.accountLogo);
            localStorage.setItem("accountName", tenant.accountName);
            const response = await LoginService.validateAuth(postValidate);

            if (response.status === "SUCCESS") {
                if (isDesktopOAuth()) {
                    try {
                        await this.finishDesktopLogin(response?.token);
                    } catch (err) {
                        showNotificationGlobal(
                            err?.message || "Could not return to SuperAgentX Desktop. Try again.",
                            "error"
                        );
                    }
                    return;
                }

                localStorage.setItem(ACCESS_TOKEN, response?.token);
                const decoded = jwtDecode(response?.token);
                localStorage.setItem("Uid", decoded?.id);
                if (decoded?.roles?.length > 0) {
                    localStorage.setItem("role", decoded.roles[0]);
                }
                this.getUserDetails();
                showNotificationGlobal("Logged in successfully.", "success");
                setTimeout(() => this.props.navigate("/apps"), 1000);
            } else if (response.status === "FAILURE") {
                if (response.token) {
                    showNotificationGlobal(response.errorMessage, "error");
                    localStorage.setItem("Login_State", JSON.stringify(false));
                    localStorage.setItem(ACCESS_TOKEN, response.token);
                    const decoded = jwtDecode(response.token);
                    localStorage.setItem("Uid", decoded?.id);
                    if (decoded?.roles?.length > 0) {
                        localStorage.setItem("role", decoded.roles[0]);
                    }
                    this.props.navigate("/auth/update-password");
                } else {
                    showNotificationGlobal(response?.errorMessage, "error");
                }
            }
        } catch (error) {
            showNotificationGlobal("Failed to validate tenant. Please try again.", "error");
        } finally {
            this.setState({ selectedTenant: null });
        }
    };

    getUserDetails = () => {
        UserService.getuserdetails()
            .then(res => {
                localStorage.setItem("profile", JSON.stringify(res));
                localStorage.setItem("accountId", res.accountId);
                localStorage.setItem("tenant", res.tenant);
            })
            .catch(err => console.error('Error getting user details:', err));
    };

    getAvatarColor = (name) => {
        const colors = [
            ['#6366f1', '#818cf8'],
            ['#0ea5e9', '#38bdf8'],
            ['#10b981', '#34d399'],
            ['#f59e0b', '#fbbf24'],
            ['#ec4899', '#f472b6'],
        ];
        const idx = (name?.charCodeAt(0) || 0) % colors.length;
        return colors[idx];
    };

    render() {
        const { tenants, loading, timeLeft, userEmail, error, tenantSearch, selectedTenant } = this.state;
        const tenantList = tenants || [];
        const filteredTenants = this.filterTenants(tenantList, tenantSearch);
        const urgency = this.getTimerUrgency();

        if (!sessionStorage.getItem(SIGNIN_AUTH_TOKEN)) return null;

        const styles = `
            @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');
            *,*::before,*::after{box-sizing:border-box;}
            .ct-root{min-height:100vh;display:flex;align-items:center;justify-content:center;background:#f0f4ff;font-family:'Plus Jakarta Sans',sans-serif;padding:24px 16px;position:relative;overflow:hidden;}
            .ct-blob{position:fixed;border-radius:50%;filter:blur(90px);pointer-events:none;z-index:0;}
            .ct-blob-1{width:480px;height:480px;top:-140px;left:-100px;background:rgba(199,210,254,0.55);}
            .ct-blob-2{width:360px;height:360px;bottom:-80px;right:-80px;background:rgba(186,230,253,0.45);}
            .ct-blob-3{width:260px;height:260px;top:50%;right:15%;background:rgba(221,214,254,0.35);}
            @keyframes cardIn{from{opacity:0;transform:translateY(20px) scale(0.98)}to{opacity:1;transform:translateY(0) scale(1)}}
            .ct-card{position:relative;z-index:1;width:100%;max-width:500px;background:#ffffff;border:1px solid #e0e7ff;border-radius:24px;padding:40px 36px 32px;box-shadow:0 8px 32px rgba(99,102,241,0.09),0 2px 8px rgba(0,0,0,0.04);animation:cardIn 0.5s cubic-bezier(0.22,1,0.36,1) both;}
            .ct-card::before{content:'';position:absolute;top:0;left:10%;right:10%;height:3px;background:linear-gradient(90deg,#818cf8,#6366f1,#a78bfa);border-radius:0 0 6px 6px;}
            .ct-timer{position:absolute;top:18px;right:22px;display:flex;align-items:center;gap:6px;padding:5px 11px;border-radius:20px;font-family:'JetBrains Mono',monospace;font-size:12px;font-weight:500;transition:all 0.3s;}
            .ct-timer-normal{background:#eef2ff;color:#4338ca;border:1px solid #c7d2fe;}
            .ct-timer-warning{background:#fffbeb;color:#b45309;border:1px solid #fcd34d;}
            .ct-timer-urgent{background:#fef2f2;color:#b91c1c;border:1px solid #fca5a5;animation:timerPulse 0.9s ease infinite;}
            .ct-timer-dot{width:5px;height:5px;border-radius:50%;background:currentColor;}
            .ct-timer-normal .ct-timer-dot{animation:blinkSlow 2s ease infinite;}
            .ct-timer-urgent .ct-timer-dot{animation:blinkFast 0.5s ease infinite;}
            @keyframes blinkSlow{0%,100%{opacity:1}50%{opacity:.25}}
            @keyframes blinkFast{0%,100%{opacity:1}50%{opacity:.1}}
            @keyframes timerPulse{0%,100%{box-shadow:0 0 0 0 rgba(185,28,28,.12)}50%{box-shadow:0 0 0 4px rgba(185,28,28,.06)}}
            .ct-header{text-align:center;margin-bottom:26px;}
            .ct-logo-wrap{display:inline-block;position:relative;margin-bottom:10px;}
            .ct-logo{width:200px;}
            .ct-badge{position:absolute;top:-5px;right:-38px;font-size:8.5px;font-weight:700;letter-spacing:0.8px;text-transform:uppercase;padding:3px 7px;border-radius:6px;background:#eef2ff;color:#4338ca;border:1px solid #c7d2fe;}
            .ct-headline{font-size:20px;font-weight:700;color:#1e1b4b;margin:6px 0 3px;letter-spacing:-0.3px;}
            .ct-subline{font-size:13px;color:#6b7280;}
            .ct-desktop-banner{display:flex;align-items:center;gap:10px;background:#eef2ff;border:1px solid #c7d2fe;border-radius:12px;padding:10px 12px;margin-bottom:16px;color:#3730a3;font-size:12.5px;font-weight:600;}
            .ct-desktop-banner i{font-size:14px;}
            .ct-user-pill{display:flex;align-items:center;gap:10px;background:#f8faff;border:1px solid #e0e7ff;border-radius:14px;padding:10px 14px;margin-bottom:20px;}
            .ct-user-icon{width:32px;height:32px;border-radius:9px;background:#eef2ff;display:flex;align-items:center;justify-content:center;font-size:14px;color:#6366f1;flex-shrink:0;}
            .ct-user-email{font-size:13.5px;font-weight:600;color:#374151;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
            .ct-user-count{font-size:11.5px;font-weight:600;padding:2px 9px;border-radius:8px;background:#eef2ff;color:#4338ca;border:1px solid #c7d2fe;flex-shrink:0;}
            .ct-error{display:flex;align-items:center;gap:10px;background:#fef2f2;border:1px solid #fecaca;border-radius:12px;padding:12px 16px;margin-bottom:16px;color:#991b1b;font-size:13px;}
            .ct-section-label{display:flex;align-items:center;gap:8px;font-size:10.5px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:#9ca3af;margin-bottom:10px;}
            .ct-section-label::after{content:'';flex:1;height:1px;background:#e5e7eb;}
            .ct-search-wrap{position:relative;margin-bottom:10px;}
            .ct-search-icon{position:absolute;left:13px;top:50%;transform:translateY(-50%);color:#9ca3af;font-size:13px;pointer-events:none;}
            .ct-search-input{width:100%;background:#f9fafb;border:1px solid #e5e7eb;border-radius:12px;padding:10px 38px 10px 36px;font-family:'Plus Jakarta Sans',sans-serif;font-size:13px;color:#111827;outline:none;transition:all 0.2s;}
            .ct-search-input::placeholder{color:#d1d5db;}
            .ct-search-input:focus{border-color:#a5b4fc;background:#ffffff;box-shadow:0 0 0 3px rgba(99,102,241,0.1);}
            .ct-search-clear{position:absolute;right:11px;top:50%;transform:translateY(-50%);background:none;border:none;color:#9ca3af;cursor:pointer;padding:4px;border-radius:4px;font-size:12px;display:flex;align-items:center;justify-content:center;transition:color 0.15s;}
            .ct-search-clear:hover{color:#4b5563;}
            .ct-search-meta{font-size:11.5px;color:#9ca3af;margin-bottom:8px;padding-left:2px;}
            .ct-list{display:flex;flex-direction:column;gap:7px;max-height:290px;overflow-y:auto;padding-right:2px;scrollbar-width:thin;scrollbar-color:#c7d2fe transparent;}
            .ct-list::-webkit-scrollbar{width:4px;}
            .ct-list::-webkit-scrollbar-thumb{background:#c7d2fe;border-radius:4px;}
            @keyframes itemIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
            .ct-tenant-item{position:relative;display:flex;align-items:center;gap:13px;padding:7px 9px;border-radius:14px;background:#ffffff;border:1px solid #e5e7eb;cursor:pointer;outline:none;transition:all 0.2s cubic-bezier(0.22,1,0.36,1);animation:itemIn 0.35s cubic-bezier(0.22,1,0.36,1) both;}
            .ct-tenant-item:nth-child(1){animation-delay:0.05s}
            .ct-tenant-item:nth-child(2){animation-delay:0.1s}
            .ct-tenant-item:nth-child(3){animation-delay:0.15s}
            .ct-tenant-item:nth-child(4){animation-delay:0.2s}
            .ct-tenant-item:nth-child(5){animation-delay:0.25s}
            .ct-tenant-item::before{content:'';position:absolute;left:0;top:0;bottom:0;width:0;transition:width 0.2s ease;}
            .ct-tenant-item:hover{background:#f5f7ff;border-color:#c7d2fe;transform:translateY(-1px);box-shadow:0 4px 12px rgba(99,102,241,0.1);}
            .ct-tenant-item:hover::before{width:3px;}
            .ct-tenant-item:focus-visible{border-color:#818cf8;box-shadow:0 0 0 3px rgba(99,102,241,0.15);}
            .ct-tenant-item--loading{opacity:0.6;pointer-events:none;}
            .ct-avatar{width:42px;height:42px;border-radius:11px;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:16px;color:white;overflow:hidden;}
            .ct-avatar img{width:100%;height:100%;object-fit:cover;}
            .ct-tenant-text{flex:1;min-width:0;}
            .ct-tenant-name{font-size:14px;font-weight:600;color:#111827;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-bottom:2px;}
            .ct-tenant-hint{font-size:11.5px;color:#9ca3af;}
            .ct-tenant-action{width:30px;height:30px;border-radius:9px;background:#eef2ff;display:flex;align-items:center;justify-content:center;color:#6366f1;font-size:12px;flex-shrink:0;transition:all 0.2s;}
            .ct-tenant-item:hover .ct-tenant-action{background:#6366f1;color:white;transform:translateX(2px);}
            .ct-spinner{width:15px;height:15px;border:2px solid #c7d2fe;border-top-color:#6366f1;border-radius:50%;animation:spin 0.7s linear infinite;}
            @keyframes spin{to{transform:rotate(360deg)}}
            .ct-empty{text-align:center;padding:30px 16px;color:#9ca3af;}
            .ct-empty-icon{font-size:26px;margin-bottom:10px;opacity:0.5;}
            .ct-empty-title{font-size:14px;font-weight:600;color:#374151;margin-bottom:4px;}
            .ct-empty-text{font-size:12.5px;margin-bottom:12px;}
            .ct-clear-btn{background:#eef2ff;border:1px solid #c7d2fe;border-radius:8px;padding:6px 14px;font-size:12.5px;font-family:'Plus Jakarta Sans',sans-serif;color:#4338ca;cursor:pointer;font-weight:500;transition:all 0.2s;}
            .ct-clear-btn:hover{background:#e0e7ff;border-color:#a5b4fc;}
            .ct-skeleton-item{display:flex;align-items:center;gap:13px;padding:12px 13px;border-radius:14px;background:#f9fafb;border:1px solid #f3f4f6;margin-bottom:7px;}
            .ct-skeleton-avatar{width:42px;height:42px;border-radius:11px;background:#e5e7eb;animation:shimmer 1.5s ease-in-out infinite;}
            .ct-skeleton-lines{flex:1;}
            .ct-skeleton-line{height:10px;border-radius:6px;background:#e5e7eb;animation:shimmer 1.5s ease-in-out infinite;}
            .ct-skeleton-line:first-child{width:60%;margin-bottom:8px;}
            .ct-skeleton-line:last-child{width:35%;}
            @keyframes shimmer{0%,100%{opacity:.6}50%{opacity:1}}
            .ct-divider{height:1px;background:#f3f4f6;margin:22px 0 18px;}
            .ct-footer{display:flex;flex-direction:column;align-items:center;gap:10px;}
            .ct-cancel-btn{background:transparent;border:1px solid #e5e7eb;border-radius:12px;padding:9px 26px;font-family:'Plus Jakarta Sans',sans-serif;font-size:13px;font-weight:500;color:#6b7280;cursor:pointer;transition:all 0.2s;}
            .ct-cancel-btn:hover{background:#f9fafb;border-color:#d1d5db;color:#374151;}
            .ct-copyright{font-size:11px;color:#d1d5db;letter-spacing:0.2px;}
            @media(max-width:560px){.ct-card{padding:32px 18px 24px;border-radius:18px;}.ct-logo{width:160px;}.ct-headline{font-size:18px;}}
        `;

        return (
            <>
                <style>{styles}</style>

                <div className="ct-root">
                    <div className="ct-blob ct-blob-1" />
                    <div className="ct-blob ct-blob-2" />
                    <div className="ct-blob ct-blob-3" />

                    <div className="ct-card">
                        {/* Timer */}
                        <div className={`ct-timer ct-timer-${urgency}`}>
                            <span className="ct-timer-dot" />
                            {this.formatTime(timeLeft)}
                        </div>

                        {/* Header */}
                        <div className="ct-header">
                            <div className="ct-logo-wrap">
                                <img src={logo} className="ct-logo" alt="SuperAgentX" />
                            </div>
                            <div className="ct-headline">Choose Your Organization</div>
                            <div className="ct-subline">
                                {isDesktopOAuth()
                                    ? "Select a workspace, then return to SuperAgentX Desktop"
                                    : "Select a workspace to continue"}
                            </div>
                        </div>

                        {isDesktopOAuth() ? (
                            <div className="ct-desktop-banner">
                                <i className="fa-solid fa-desktop" aria-hidden="true" />
                                Signing in to SuperAgentX Desktop
                            </div>
                        ) : null}

                        {/* User pill */}
                        {userEmail && (
                            <div className="ct-user-pill">
                                <div className="ct-user-icon">
                                    <i className="fa-solid fa-at" />
                                </div>
                                <div className="ct-user-email">{userEmail}</div>
                                <div className="ct-user-count">
                                    {tenantList.length} {tenantList.length === 1 ? 'org' : 'orgs'}
                                </div>
                            </div>
                        )}

                        {/* Error */}
                        {error && (
                            <div className="ct-error">
                                <i className="fa-solid fa-circle-exclamation" />
                                <span>{error}</span>
                                <button
                                    onClick={() => this.props.navigate('/auth/login')}
                                    style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#991b1b', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', textDecoration: 'underline' }}
                                >
                                    Back to Login
                                </button>
                            </div>
                        )}

                        {/* Section label */}
                        <div className="ct-section-label">
                            <i className="fa-solid fa-building" style={{ fontSize: 9 }} />
                            Organizations
                        </div>

                        {/* Loading skeletons */}
                        {loading && [1, 2, 3].map(i => (
                            <div key={i} className="ct-skeleton-item">
                                <div className="ct-skeleton-avatar" />
                                <div className="ct-skeleton-lines">
                                    <div className="ct-skeleton-line" />
                                    <div className="ct-skeleton-line" />
                                </div>
                            </div>
                        ))}

                        {/* Tenant list */}
                        {!loading && tenantList.length > 0 && (
                            <>
                                <div className="ct-search-wrap">
                                    <i className="fa-solid fa-magnifying-glass ct-search-icon" />
                                    <input
                                        type="search"
                                        className="ct-search-input"
                                        placeholder="Search organizations…"
                                        value={tenantSearch}
                                        onChange={e => this.setState({ tenantSearch: e.target.value })}
                                        autoComplete="off"
                                        spellCheck="false"
                                    />
                                    {tenantSearch && (
                                        <button className="ct-search-clear" onClick={this.clearTenantSearch} aria-label="Clear">
                                            <i className="fa-solid fa-xmark" />
                                        </button>
                                    )}
                                </div>

                                {tenantSearch.trim() && (
                                    <div className="ct-search-meta">
                                        {filteredTenants.length} of {tenantList.length} matching
                                    </div>
                                )}

                                <div className="ct-list" role="list">
                                    {filteredTenants.length > 0 ? (
                                        filteredTenants.map((tenant, index) => {
                                            const [c1, c2] = this.getAvatarColor(tenant.accountName);
                                            const isLoading = selectedTenant === tenant.accountID;
                                            return (
                                                <div
                                                    key={tenant.id || tenant.accountID || index}
                                                    role="button"
                                                    tabIndex={0}
                                                    className={`ct-tenant-item${isLoading ? ' ct-tenant-item--loading' : ''}`}
                                                    onClick={() => this.handleTenantSelect(tenant)}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.handleTenantSelect(tenant); }
                                                    }}
                                                >
                                                    <div className="ct-avatar" style={{ background: `linear-gradient(135deg, ${c1}, ${c2})` }}>
                                                        {tenant.accountLogo
                                                            ? <img src={tenant.accountLogo} alt="" />
                                                            : (tenant.accountName?.charAt(0)?.toUpperCase() || '?')
                                                        }
                                                    </div>
                                                    <div className="ct-tenant-text">
                                                        <div className="ct-tenant-name">{tenant.accountName}</div>
                                                        <div className="ct-tenant-hint">{isLoading ? 'Connecting…' : 'Continue to workspace'}</div>
                                                    </div>
                                                    <div className="ct-tenant-action">
                                                        {isLoading
                                                            ? <div className="ct-spinner" />
                                                            : <i className="fa-solid fa-arrow-right" />
                                                        }
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="ct-empty">
                                            <div className="ct-empty-icon"><i className="fa-solid fa-filter-circle-xmark" /></div>
                                            <div className="ct-empty-title">No results</div>
                                            <div className="ct-empty-text">Nothing matches "{tenantSearch.trim()}"</div>
                                            <button className="ct-clear-btn" onClick={this.clearTenantSearch}>Clear search</button>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        {/* No tenants at all */}
                        {!loading && tenantList.length === 0 && (
                            <div className="ct-empty">
                                <div className="ct-empty-icon"><i className="fa-solid fa-folder-open" /></div>
                                <div className="ct-empty-title">No organizations found</div>
                                <div className="ct-empty-text">Contact your administrator if you expected to see one here.</div>
                            </div>
                        )}

                        <div className="ct-divider" />

                        <div className="ct-footer">
                            <button className="ct-cancel-btn" onClick={this.handleCancel}>
                                Cancel &amp; Sign Out
                            </button>
                            <span className="ct-copyright">
                                © {new Date().getFullYear()} SuperAgentX. All rights reserved.
                            </span>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

export default withNavigate(ChooseTenant);